import { getXrplClient } from './client'
import { fromXrplAmount, toXrplAsset } from './amounts'
import { RIPPLE_DATA_API_BASE, XRPSCAN_API_BASE } from './config'
import { XrplCurrency, XrplAmount } from './types'

const PRICE_CACHE_TTL_MS = 20000
const XRP_USD_TTL_MS = 60000

const priceCache = new Map<string, { value: number | null; fetchedAt: number; inFlight?: Promise<number | null> }>()
let cachedXrpUsd: { value: number | null; fetchedAt: number; inFlight?: Promise<number | null> } = {
  value: null,
  fetchedAt: 0,
}

const now = () => Date.now()

const parseAmountValue = (amount: XrplAmount | undefined) => {
  if (!amount) return null
  const value = Number(fromXrplAmount(amount))
  return Number.isFinite(value) && value > 0 ? value : null
}

const RLUSD_ISSUER = 'rMxCKbEDwqr76QuheSUMdEGf4B9xJ8m5De'
const RLUSD_CURRENCY = '524C555344000000000000000000000000000000'

const fetchXrpUsdRate = async () => {
  if (cachedXrpUsd.value && now() - cachedXrpUsd.fetchedAt < XRP_USD_TTL_MS) {
    return cachedXrpUsd.value
  }
  if (cachedXrpUsd.inFlight) {
    return cachedXrpUsd.inFlight
  }

  cachedXrpUsd.inFlight = (async () => {
    try {
      const base = RIPPLE_DATA_API_BASE.endsWith('/') ? RIPPLE_DATA_API_BASE : `${RIPPLE_DATA_API_BASE}/`
      const response = await fetch(`${base}exchange_rates/XRP/USD`)
      if (response.ok) {
        const data = await response.json()
        const rate = Number(data?.rate ?? data?.result?.rate ?? data?.exchange_rates?.[0]?.rate)
        if (Number.isFinite(rate) && rate > 0) {
          cachedXrpUsd = { value: rate, fetchedAt: now() }
          return rate
        }
      }
    } catch (error) {
      // ignore ripple-data failures
    }

    try {
      const base = XRPSCAN_API_BASE.endsWith('/') ? XRPSCAN_API_BASE : `${XRPSCAN_API_BASE}/`
      const response = await fetch(`${base}token/RLUSD.${RLUSD_ISSUER}`)
      if (!response.ok) {
        return null
      }
      const data = await response.json()
      const rlusdPriceXrp = Number(data?.price ?? data?.metrics?.price)
      if (Number.isFinite(rlusdPriceXrp) && rlusdPriceXrp > 0) {
        const rate = 1 / rlusdPriceXrp
        cachedXrpUsd = { value: rate, fetchedAt: now() }
        return rate
      }
    } catch (error) {
      return null
    }
    return null
  })()

  const result = await cachedXrpUsd.inFlight
  cachedXrpUsd.inFlight = undefined
  return result
}

const fetchTokenPriceXrpFromAmm = async (token: XrplCurrency) => {
  try {
    const client = await getXrplClient()
    const response = await client.request({
      command: 'amm_info',
      asset: toXrplAsset(token),
      asset2: { currency: 'XRP' },
    })
    const amm = response.result.amm
    if (!amm) return null
    const amount = parseAmountValue(amm.amount)
    const amount2 = parseAmountValue(amm.amount2)
    if (!amount || !amount2) return null
    const amountIsXrp = typeof amm.amount === 'string'
    const xrpReserve = amountIsXrp ? amount : amount2
    const tokenReserve = amountIsXrp ? amount2 : amount
    if (!xrpReserve || !tokenReserve) return null
    return xrpReserve / tokenReserve
  } catch (error) {
    return null
  }
}

const fetchTokenPriceXrpFromOrderBook = async (token: XrplCurrency) => {
  try {
    const client = await getXrplClient()
    const response = await client.request({
      command: 'book_offers',
      taker_gets: toXrplAsset(token),
      taker_pays: { currency: 'XRP' },
      limit: 1,
    })
    const offer = response.result.offers?.[0]
    if (!offer) return null
    const tokenAmount = parseAmountValue(offer.taker_gets)
    const xrpAmount = parseAmountValue(offer.taker_pays)
    if (!tokenAmount || !xrpAmount) return null
    return xrpAmount / tokenAmount
  } catch (error) {
    return null
  }
}

export const fetchTokenPriceUsd = async (token: XrplCurrency): Promise<number | null> => {
  if (!token.issuer || token.isNative || token.currency === 'XRP') {
    return null
  }
  const key = `${token.currency}:${token.issuer}`
  const cached = priceCache.get(key)
  if (cached?.value && now() - cached.fetchedAt < PRICE_CACHE_TTL_MS) {
    return cached.value
  }
  if (cached?.inFlight) {
    return cached.inFlight
  }

  const inFlight = (async () => {
    const xrpUsd = await fetchXrpUsdRate()
    if (!xrpUsd) return null
    const ammPrice = await fetchTokenPriceXrpFromAmm(token)
    const priceXrp = ammPrice ?? (await fetchTokenPriceXrpFromOrderBook(token))
    if (!priceXrp) return null
    const usd = priceXrp * xrpUsd
    if (!Number.isFinite(usd) || usd <= 0) {
      return null
    }
    return usd
  })()

  priceCache.set(key, { value: cached?.value ?? null, fetchedAt: cached?.fetchedAt ?? 0, inFlight })
  const result = await inFlight
  priceCache.set(key, { value: result, fetchedAt: now() })
  return result
}
